import React, { useState, useEffect } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { loginAsOrganisation, logout } from "../../redux/auth/authSlice";
import { postApi } from "../../api/api_instance";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { API_URLS } from "../../api/apiUrls";
import { CheckOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import PopupCard from "./PopupCard";
import PermissionForm from "./PermissionForm"; // Add your PermissionForm import
import "./Auth.css";

const LoginC = ({ LoginConClose, onSignup, onResetPassword }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { organisationLogin } = useSelector((state) => state.auth);

  useEffect(() => {
    if (organisationLogin) {
      navigate("/organization/dashboard");
      if (LoginConClose) {
        LoginConClose();
      }
    }
  }, [organisationLogin, navigate, LoginConClose]);

  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [loginFail, setLoginFail] = useState(false);
  const [showPermissionForm, setShowPermissionForm] = useState(false); // New state
  const [popupData, setPopupData] = useState({
    type: "success",
    title: "Success",
    message: "",
  });

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData({ ...loginData, [name]: value });
  };

  const triggerPopup = (type, title, message, duration = 5000) => {
    setPopupData({ type, title, message });
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), duration);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      triggerPopup(
        "error",
        "Verification Failed",
        "Please enter both email and password",
      );
      return;
    }

    try {
      const response = await postApi(
        {
          apiUrl: API_URLS.LOGIN_CLIENT,
          data: loginData,
        },
        false,
      );

      if (response.success) {
        const { user, token } = response.data;
        dispatch(loginAsOrganisation({ user, token }));

        Modal.success({
          icon: null,
          centered: true,
          closable: false,
          className: "custom-success-modal",
          okButtonProps: { style: { display: "none" } },
          content: (
            <div className="flex flex-col items-center text-center -mt-6 -mx-6 -mb-6">
              {/* Green Header */}
              <div className="w-full bg-[#06513D] px-6 py-3 text-white text-center font-semibold text-lg">
                Welcome To Carburn!
              </div>

              {/* Modal Body */}
              <div className="w-full bg-white py-4">
                <div className="bg-[#06513D] p-3 rounded-full mx-auto w-fit mb-6">
                  <CheckOutlined style={{ fontSize: "40px", color: "white" }} />
                </div>
                <p className="text-[#06513D] text-base mb-6 font-medium">
                  Empowering your Net Zero journey
                </p>
              </div>
            </div>
          ),
        });

        navigate("/organization/dashboard");

        setTimeout(() => {
          Modal.destroyAll(); // Close all modals
          setShowPermissionForm(true); // Show permission form after modal closes
        }, 2000);

        setLoginSuccess(true);
      } else {
        triggerPopup(
          "error",
          "Verification Failed",
          response.message || "Invalid credentials",
        );
        setLoginFail(true);
      }
    } catch (error) {
      triggerPopup("error", "Verification Failed", "Login Failed");
      setLoginFail(true);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    dispatch(logout());
    triggerPopup("success", "Success", "Logged out successfully!");
    navigate("/organization/login");
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white py-8 px-6 shadow-lg rounded-lg sm:px-10 w-full max-w-md relative">
          <button
            onClick={LoginConClose}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>

          <h2 className="text-center text-2xl font-bold text-[#06513D] mb-2">
            CARBURN
          </h2>
          <p className="text-center text-sm text-gray-600 mb-8">
            Carbon Footprint & ESG Reporting
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
            Welcome Back
          </h3>

          {organisationLogin ? (
            <div className="text-center">
              <p className="text-gray-700 text-base mb-4">
                You are already logged in.
              </p>
              <button
                className="text-sm text-blue-700 hover:underline"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleLogin}>
              {/* Email Field */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={loginData.email}
                  onChange={handleLoginChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-[#06513D] sm:text-sm"
                />
              </div>

              {/* Password Field */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Password
                  </label>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-[#06513D] sm:text-sm"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    className="text-sm text-[#06513D]"
                    onClick={onResetPassword}
                  >
                    Forgot password
                  </button>
                </div>
              </div>

              {/* Remember Me */}
              <div className="flex items-center custom-margin-top">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 mr-2"
                  style={{ accentColor: "#06513D" }}
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label
                  htmlFor="remember-me"
                  className="ml-2 block text-sm text-gray-700"
                >
                  Remember me for 30 days
                </label>
              </div>

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#06513D] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                >
                  Sign In
                </button>
              </div>
            </form>
          )}

          {/* Sign Up Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{" "}
              <button
                type="button"
                className="font-medium text-[#06513D]"
                onClick={onSignup}
              >
                Sign up
              </button>
            </p>
          </div>
        </div>
        {showPopup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <PopupCard
              type={popupData.type}
              title={popupData.title}
              message={popupData.message}
            />
          </div>
        )}
      </div>

      {/* Permission Form - Call this after popup closes */}
      {showPermissionForm && (
        <PermissionForm onClose={() => setShowPermissionForm(false)} />
      )}
    </>
  );
};

export default LoginC;
